import { ContainerClient } from '@azure/storage-blob';
import { addMinutes } from 'date-fns';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useCreateImageUploadSession } from '../api/hooks/images';
import { UploadProvider } from './UploadProvider';
import { UploadSession, UploadStatus } from './utils';

export interface BlobUploadSession extends UploadSession {
  meta: {
    id: string;
    metadata: Record<string, string>;
    extension: string;
    baseUrl: string;
    type: 'thumbnail' | 'original';
  };
  result: string;
}

interface AdditionalSessionArgs {
  map: Map<string, { id: string; metadata: Record<string, string>; extension: string; type: 'thumbnail' | 'original' }>;
  baseUrl: string;
}

export const BlobUploadProvider: FC<PropsWithChildren> = ({ children }) => {
  const createUploadSession = useCreateImageUploadSession();

  return useMemo(
    () => (
      <UploadProvider
        options={{
          chunkSize: 1024 * 1024 * 4, // 4MB
          chunkedUploadThreshold: Infinity
        }}
        createUploadSession={async (name, file, args: AdditionalSessionArgs) => {
          const url = await createUploadSession.mutateAsync({});
          const parent = args.map.get(name);
          if (!parent) throw new Error('Missing parent id');

          return {
            id: crypto.randomUUID(),
            name: name,
            size: file.size,
            url: url,
            created: new Date(),
            status: UploadStatus.Waiting,
            expiration: addMinutes(new Date(), 30),
            meta: {
              id: parent.id,
              metadata: parent.metadata,
              extension: parent.extension,
              baseUrl: args.baseUrl,
              type: parent.type
            }
          };
        }}
        uploadFile={async (session: BlobUploadSession, file) => {
          console.log(`Uploading ${session.name} with ${Object.keys(session.meta.metadata).join(', ')} metadata`);
          const containerClient = new ContainerClient(session.url);
          const blobClient = containerClient.getBlockBlobClient(`${session.name}.${session.meta.extension}`);
          await blobClient.uploadData(file, { metadata: session.meta.metadata });
        }}
        completeUpload={async (session: BlobUploadSession) => {
          const containerClient = new ContainerClient(session.url);
          const blobClient = containerClient.getBlockBlobClient(`${session.name}.${session.meta.extension}`);
          const blockList = await blobClient.getBlockList('uncommitted');
          const blockIds = blockList?.uncommittedBlocks?.map(block => block.name);
          if (blockIds && blockIds.length > 0) {
            await blobClient.commitBlockList(blockIds);
          }
          return `${session.meta.baseUrl}/${session.name}.${session.meta.extension}`;
        }}
        destroyUpload={async (session: BlobUploadSession) => {
          const containerClient = new ContainerClient(session.url);
          const blobClient = containerClient.getBlockBlobClient(`${session.name}.${session.meta.extension}`);
          await blobClient.delete();
        }}
        uploadChunk={async (session: BlobUploadSession, chunk, { start, end }) => {
          throw new Error('Not implemented');
          const containerClient = new ContainerClient(session.url);
          const blobClient = containerClient.getBlockBlobClient(`${session.name}.${session.meta.extension}`);
          await blobClient.stageBlock(`${session.id}-${start}-${end}`, chunk, chunk.byteLength);
        }}
      >
        {children}
      </UploadProvider>
    ),
    []
  );
};
